/* istanbul ignore file */
import { createSelector } from 'reselect';
import {
  SENSOR_TYPES,
  COLLABORATION_PROVIDERS,
  PM_PROVIDERS,
  SECURITY_PROVIDERS,
  GIT_PROVIDERS,
  PROVIDERS_MAP
} from 'common/constants/constants';
import { SENSOR_NAMES, SENSOR_STATES } from '@linearb/linjs-common';
import _ from 'lodash';
import sensorAuthorizationStates from 'common/constants/sensorAuthorizationStates';
import {
  calculateRateLimitCount,
  RATE_LIMIT_ISSUE_THRESHOLD
} from 'common/hooks/useSensorAuthRateLimitCounts';
import { isSensorOAuth2 } from 'common/services/sensors.service';

export const getSensorAuthorizations = state => state.sensorAuthorizations;

export const isProjectManagementConnectedSelector = createSelector(
  [getSensorAuthorizations],
  sensors => {
    const pmSensor = sensors?.find(
      sensor => sensor.sensor_type_id === SENSOR_TYPES.PM
    );
    const isOAuth2 = isSensorOAuth2(pmSensor);
    return (
      pmSensor && (pmSensor.state !== SENSOR_STATES.inProgress || isOAuth2)
    );
  }
);

export const gitIntegrationsSelector = createSelector(
  [getSensorAuthorizations],
  sensors => {
    return sensors?.filter?.(
      ({ sensor_type_id }) => sensor_type_id === SENSOR_TYPES.GIT
    );
  }
);

export const activeGitIntegrationsSelector = createSelector(
  [gitIntegrationsSelector],
  gitIntegrations => {
    return gitIntegrations.filter(
      ({ state }) => state === sensorAuthorizationStates.OK
    );
  }
);

export const activeGithubIntegrationsSelector = createSelector(
  [activeGitIntegrationsSelector],
  activeGitIntegrations => {
    return activeGitIntegrations.filter(
      ({ sensor_id }) => sensor_id === GIT_PROVIDERS.GITHUB
    );
  }
);

export const activeGithubCloudIntegrationsSelector = createSelector(
  [activeGitIntegrationsSelector],
  activeGitIntegrations => {
    return activeGitIntegrations.filter(
      ({ sensor_id, is_self_hosted }) =>
        sensor_id === GIT_PROVIDERS.GITHUB && !is_self_hosted
    );
  }
);

export const gitConnectedSensorIdsSelector = createSelector(
  [activeGitIntegrationsSelector],
  (activeGitIntegrations = []) => {
    const connectedProviders = {};
    activeGitIntegrations.forEach(gitIntegration => {
      const sensor_id = _.get(gitIntegration, 'sensor_id');
      if (!sensor_id || connectedProviders[sensor_id]) return;
      connectedProviders[sensor_id] = sensor_id;
    });
    return _.values(connectedProviders) || [];
  }
);

export const sensorAuthorizationsByIdSelector = createSelector(
  [getSensorAuthorizations],
  sensors => {
    return _.keyBy(sensors, 'id');
  }
);

export const pmIntegrationSelector = createSelector(
  [getSensorAuthorizations],
  sensors => sensors.find(sensor => sensor.sensor_type_id === SENSOR_TYPES.PM)
);

export const pmSensorAuthIdSelector = createSelector(
  [pmIntegrationSelector],
  pmIntegrations => _.get(pmIntegrations, 'id')
);

export const pmToolSensorIdSelector = createSelector(
  [pmIntegrationSelector],
  pmTool => _.get(pmTool, 'sensor_id')
);

export const isPmToolAzureBoardsSelector = createSelector(
  [pmIntegrationSelector],
  pmTool => _.get(pmTool, 'sensor_id') === PM_PROVIDERS.AZURE_BOARDS
);

export const isPmToolJiraSelector = createSelector(
  [pmIntegrationSelector],
  pmTool => _.get(pmTool, 'sensor_id') === PM_PROVIDERS.JIRA
);

export const pmToolDefinitionSelector = createSelector(
  [pmToolSensorIdSelector],
  pmToolsSensorId =>
    _.find(PROVIDERS_MAP, provider => provider.sensor_id === pmToolsSensorId)
);

export const getSlackSensorAuthorizationSelector = createSelector(
  [getSensorAuthorizations],
  sensors =>
    sensors.find(sensor => sensor.sensor_id === COLLABORATION_PROVIDERS.SLACK)
);

export const getFailingSensorAuthorizationWithoutAzureAndClubhouse =
  createSelector(
    [getSensorAuthorizations],
    // remove microsoft teams and clubhouse because they are still not implemented
    sensorAuthorizations =>
      sensorAuthorizations.filter(
        sensorAuthorization =>
          _.get(sensorAuthorization, 'state') === SENSOR_STATES.failing &&
          _.get(sensorAuthorization, 'sensor_name', '') !==
            SENSOR_NAMES.MS_TEAMS &&
          _.get(sensorAuthorization, 'sensor_name', '') !==
            SENSOR_NAMES.CLUBHOUSE
      )
  );

export const getJiraSensorAuthorizationSelector = createSelector(
  [getSensorAuthorizations],
  sensors => sensors.find(sensor => sensor.sensor_id === PM_PROVIDERS.JIRA)
);

export const isSlackConnectedSelector = createSelector(
  [getSlackSensorAuthorizationSelector],
  slackSensor => !!slackSensor
);

export const getMsTeamsSensorAuthorizationSelector = createSelector(
  [getSensorAuthorizations],
  sensors =>
    sensors.find(sensor => sensor.sensor_id === COLLABORATION_PROVIDERS.MSTEAMS)
);

export const getSamlSensorAuthorization = createSelector(
  [getSensorAuthorizations],
  sensors =>
    sensors.find(sensor => sensor.sensor_id === SECURITY_PROVIDERS.SAML)
);

export const getIsAllowedToConfigureWorkerBInlineApprovalSelector =
  createSelector([getSensorAuthorizations], sensors =>
    sensors.some(
      sensor =>
        sensor.sensor_id === GIT_PROVIDERS.GITHUB &&
        sensor.state === SENSOR_STATES.ok &&
        !sensor.is_pat
    )
  );

export const isGitStreamConnectedToSensorSelector = sensorAuthId =>
  createSelector(getSensorAuthorizations, sensorAuthorizations =>
    sensorAuthorizations
      .filter(sensorAuth => sensorAuth.id === sensorAuthId)
      .some(sensorAuth => sensorAuth.gitstream_enabled === true)
  );

export const sensorsWithRateLimitSelector = createSelector(
  [getSensorAuthorizations],
  sensors => {
    const sensorsWithRateLimit = sensors.filter(sensor => {
      const sensorRateLimit = calculateRateLimitCount(sensor);
      return sensorRateLimit > RATE_LIMIT_ISSUE_THRESHOLD;
    });

    return sensorsWithRateLimit;
  }
);
